<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import { Camera, CubeView, DropPhoto, FitToScreen } from '@vicons/carbon';
import {
  CameraProjectionType,
  EditorPanelShowPropertyReason,
  ModelDisplayMode,
  RawPointerEventType, StandardViewType,
  Viewer,
} from '@puppycad/cad-core';
import { useViewer } from '@/hooks/useViewer';
import {
  notifySectionViewUiStateChanged,
  resolveSectionViewController,
} from '@/utils';
import { requestEditorPropertyTab } from './requestEditorPropertyTab';
import type { ViewportToolbarProps } from '../../../types/editor';

const props = withDefaults(defineProps<ViewportToolbarProps>(), {
  readonlyPreview: false,
});

/**
 * 快速视图按钮中的立方体预览朝向。
 */
enum QuickViewOrientationPreviewKind {
  /** 前视图预览。 */
  Front = 'front',
  /** 后视图预览。 */
  Back = 'back',
  /** 左视图预览。 */
  Left = 'left',
  /** 右视图预览。 */
  Right = 'right',
  /** 上视图预览。 */
  Top = 'top',
  /** 下视图预览。 */
  Bottom = 'bottom',
}

//------ 显示模型下拉 开始------
/**
 * 显示模型下拉选项。
 */
interface DisplayModelModeItem {
  /** 显示模型模式。 */
  mode: ModelDisplayMode;
  /** 中文标签。 */
  label: string;
}
//------ 显示模型下拉 结束------

//------ 整屏显示全图 开始------
/**
 * 视口工具栏快捷键集合。
 */
enum ViewportToolbarShortcutKey {
  /** 整屏显示全图。 */
  FitVisibleItems = 'f',
}
//------ 整屏显示全图 结束------

/**
 * 快速标准视图切换项。
 */
interface QuickViewOrientationItem {
  /**
   * cad-core 标准视图类型。
   */
  type: StandardViewType;
  /**
   * 中文标签。
   */
  label: string;
  /**
   * 英文短标签。
   */
  shortLabel: string;
  /**
   * 预览立方体朝向。
   */
  previewKind: QuickViewOrientationPreviewKind;
}

const { setViewerCallback, getViewer } = useViewer();

//------ 默认正交相机 开始------
/**
 * 当前视图投影模式。
 *
 * 设计说明：
 * 1. UI 默认值必须与 cad-core 的默认活动相机保持一致；
 * 2. 使用 cad-core 统一枚举，避免面板状态与 SDK 投影协议出现字符串分叉。
 */
const viewMode = ref<CameraProjectionType>(CameraProjectionType.Orthographic);
//------ 默认正交相机 结束------
//------ 显示模型下拉 开始------
/**
 * 当前选中的显示模型模式。
 *
 * 设计说明：SolidWorks 常用默认显示为带边线上色，因此 UI 初始态与该习惯保持一致。
 */
const displayModelMode = ref<ModelDisplayMode>(ModelDisplayMode.ShadedWithEdges);
//------ 显示模型下拉 结束------
const isViewOrientationVisible = ref(false);
const currentStandardViewType = ref<StandardViewType | null>(null);
const rawPointerEventSubscriptions: Subscription[] = [];
let disposePendingViewerCallback: (() => void) | null = null;
let viewerCreatedSubscription: Subscription | null = null;
let unsubscribeSectionViewStateChanged: (() => void) | null = null;
const isSectionViewEnabled = ref(false);
const isSectionViewEditing = ref(false);
const hasSectionViewDefinition = ref(false);

//------ 显示模型下拉 开始------
/**
 * 显示模型下拉项集合。
 */
const displayModelModeItems: readonly DisplayModelModeItem[] = [
  {
    mode: ModelDisplayMode.ShadedWithEdges,
    label: '带边线上色',
  },
  {
    mode: ModelDisplayMode.Shaded,
    label: '上色',
  },
  {
    mode: ModelDisplayMode.Unshaded,
    label: '不着色',
  },
] as const;

/**
 * 当前显示模型下拉项。
 */
const currentDisplayModelModeItem = computed<DisplayModelModeItem>(() => {
  return displayModelModeItems.find((item) => item.mode === displayModelMode.value)
    ?? displayModelModeItems[0];
});
//------ 显示模型下拉 结束------

const quickViewOrientationItems: readonly QuickViewOrientationItem[] = [
  {
    type: StandardViewType.Front,
    label: '前视',
    shortLabel: 'Front',
    previewKind: QuickViewOrientationPreviewKind.Front,
  },
  {
    type: StandardViewType.Back,
    label: '后视',
    shortLabel: 'Back',
    previewKind: QuickViewOrientationPreviewKind.Back,
  },
  {
    type: StandardViewType.Left,
    label: '左视',
    shortLabel: 'Left',
    previewKind: QuickViewOrientationPreviewKind.Left,
  },
  {
    type: StandardViewType.Right,
    label: '右视',
    shortLabel: 'Right',
    previewKind: QuickViewOrientationPreviewKind.Right,
  },
  {
    type: StandardViewType.Top,
    label: '上视',
    shortLabel: 'Top',
    previewKind: QuickViewOrientationPreviewKind.Top,
  },
  {
    type: StandardViewType.Bottom,
    label: '下视',
    shortLabel: 'Bottom',
    previewKind: QuickViewOrientationPreviewKind.Bottom,
  },
] as const;

/**
 * 从 Viewer 当前相机投影类型解析工具栏投影模式。
 *
 * @param viewer 当前 Viewer 运行时。
 * @returns 工具栏可直接消费的投影模式。
 */
const resolveProjectionMode = (viewer: Viewer): CameraProjectionType => {
  return viewer.getCameraProjectionType?.() ?? CameraProjectionType.Orthographic;
};

/**
 * 同步当前投影按钮高亮状态。
 *
 * @param viewer 当前 Viewer 运行时。
 */
const syncProjectionMode = (viewer: Viewer): void => {
  viewMode.value = resolveProjectionMode(viewer);
};

//------ 显示模型下拉 开始------
/**
 * 同步显示模型模式。
 *
 * @param viewer 当前 Viewer 运行时。
 */
const syncDisplayModelMode = (viewer: Viewer): void => {
  displayModelMode.value = viewer.getModelDisplayMode();
};
//------ 显示模型下拉 结束------

/**
 * 同步视图定向入口与标准视图高亮状态。
 *
 * 说明：标准视图高亮只在视图定向立方体可见时有意义；退出定向模式后清空高亮，
 * 避免用户误以为当前普通相机姿态仍被某个标准视图锁定。
 *
 * @param viewer 当前 Viewer 运行时。
 */
const syncCurrentStandardViewType = (viewer: Viewer | null = getViewer()): void => {
  const cameraManager = viewer?.cameraManager;
  const isCurrentViewOrientationVisible = cameraManager?.isViewOrientationMeshVisible?.() ?? false;

  isViewOrientationVisible.value = isCurrentViewOrientationVisible;
  currentStandardViewType.value = isCurrentViewOrientationVisible
    ? (cameraManager?.getCurrentStandardViewType?.() ?? null)
    : null;
};

/**
 * 同步视图工具栏完整显示状态。
 *
 * @param viewer 当前 Viewer 运行时。
 */
const syncViewportToolbarState = (viewer: Viewer | null = getViewer()): void => {
  if (!viewer) {
    return;
  }

  syncProjectionMode(viewer);
  syncDisplayModelMode(viewer);
  syncCurrentStandardViewType(viewer);
};

/**
 * 释放 Viewer 原始指针事件订阅。
 */
const disposeRawPointerEventSubscriptions = (): void => {
  rawPointerEventSubscriptions.forEach((subscription) => {
    subscription.unsubscribe();
  });
  rawPointerEventSubscriptions.length = 0;
};

/**
 * 注册用于同步视图工具栏状态的 Viewer 事件订阅。
 *
 * 说明：视图定向立方体允许用户直接拖拽/点击改变标准视图，工具栏必须监听 Viewer
 * 原始点击与鼠标释放事件，才能在非按钮触发的视图变化后保持高亮状态正确。
 *
 * @param viewer 当前 Viewer 运行时。
 */
const registerViewportToolbarSubscriptions = (viewer: Viewer): void => {
  disposeRawPointerEventSubscriptions();
  unsubscribeSectionViewStateChanged?.();
  unsubscribeSectionViewStateChanged = null;
  isSectionViewEnabled.value = false;
  isSectionViewEditing.value = false;
  hasSectionViewDefinition.value = false;
  notifySectionViewUiStateChanged();

  const clickSubscription = viewer.observeRawPointerEvent?.(RawPointerEventType.Click)?.subscribe(() => {
    syncCurrentStandardViewType(viewer);
  });
  const mouseUpSubscription = viewer.observeRawPointerEvent?.(RawPointerEventType.MouseUp)?.subscribe(() => {
    syncCurrentStandardViewType(viewer);
  });

  if (clickSubscription) {
    rawPointerEventSubscriptions.push(clickSubscription);
  }
  if (mouseUpSubscription) {
    rawPointerEventSubscriptions.push(mouseUpSubscription);
  }

  const sectionViewController = resolveSectionViewController(viewer);
  if (sectionViewController) {
    const syncSectionViewState = (): void => {
      const state = sectionViewController.getState();
      isSectionViewEnabled.value = state.isEnabled;
      isSectionViewEditing.value = state.isEditing;
      hasSectionViewDefinition.value = state.hasDefinition;
      notifySectionViewUiStateChanged();
    };
    unsubscribeSectionViewStateChanged = sectionViewController.subscribeStateChanged(syncSectionViewState);
    syncSectionViewState();
  }

  syncViewportToolbarState(viewer);
};

/**
 * 切换剖面视图显示或进入首次定义会话。
 *
 * 设计说明：普通编辑态由 cad-core 的 toggle 收口创建、退出和重新进入选择会话；
 * 历史版本预览只调用可见性入口，避免只读场景清除定义或进入编辑状态。
 */
const handleSectionViewToggle = (): void => {
  if (props.readonlyPreview && !hasSectionViewDefinition.value) {
    return;
  }
  const controller = resolveSectionViewController(getViewer());
  if (props.readonlyPreview) {
    controller?.toggleVisibility();
    notifySectionViewUiStateChanged();
    return;
  }
  controller?.toggle();
  if (controller?.getState().isEditing) {
    requestEditorPropertyTab(
      EditorPanelShowPropertyReason.SectionViewCommandOpened,
      null,
      0,
    );
  }
  notifySectionViewUiStateChanged();
};

/**
 * 切换正交/透视投影模式。
 *
 * @param value 目标投影模式。
 */
const handleViewModeChange = (value: CameraProjectionType): void => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  currentViewer.setCameraProjectionType?.(value);
  viewMode.value = value;
  syncCurrentStandardViewType(currentViewer);
};

//------ 显示模型下拉 开始------
/**
 * 切换显示模型模式。
 *
 * @param mode 目标显示模型模式。
 */
const handleDisplayModelModeChange = (mode: ModelDisplayMode): void => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    displayModelMode.value = mode;
    return;
  }

  currentViewer.setModelDisplayMode(mode);
  displayModelMode.value = mode;
};
//------ 显示模型下拉 结束------

//------ 整屏显示全图 开始------
/**
 * 执行整屏显示全图命令。
 *
 * 设计说明：
 * 1. 工具栏只负责触发用户命令，不在 UI 层重复计算相机包围盒；
 * 2. 具体聚焦算法统一复用 cad-core 的 CameraManager 入口，确保按钮与快捷键行为一致；
 * 3. 该命令仅改变视图状态，不写入 CAD 主模型状态。
 */
const handleFitVisibleItems = (): void => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  currentViewer.cameraManager?.fitVisibleItems();
  syncCurrentStandardViewType(currentViewer);
};

/**
 * 判断键盘事件是否来自文本输入上下文。
 *
 * @param event 浏览器键盘事件。
 * @returns 是否应跳过工具栏快捷键处理。
 */
const isTextEditingKeyboardEvent = (event: KeyboardEvent): boolean => {
  const target = event.target as HTMLElement | null;
  if (!target) {
    return false;
  }

  // 用户正在输入文本时，F 应保持原生输入语义，不能被视口命令抢占。
  const tagName = target.tagName.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true;
  }

  // contenteditable 常用于富文本或内联编辑，同样需要保留文本输入行为。
  return target.isContentEditable;
};

/**
 * 判断键盘事件是否应被视口工具栏快捷键忽略。
 *
 * @param event 浏览器键盘事件。
 * @returns 是否忽略该快捷键事件。
 */
const shouldIgnoreViewportToolbarShortcut = (event: KeyboardEvent): boolean => {
  if (event.defaultPrevented) {
    return true;
  }

  // 组合键通常属于浏览器、系统或其它编辑命令，工具栏仅接管单键 F。
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return true;
  }

  return isTextEditingKeyboardEvent(event);
};

/**
 * 处理视口工具栏全局快捷键。
 *
 * @param event 浏览器键盘事件。
 */
const handleViewportToolbarKeydown = (event: KeyboardEvent): void => {
  if (shouldIgnoreViewportToolbarShortcut(event)) {
    return;
  }

  if (event.key.toLowerCase() !== ViewportToolbarShortcutKey.FitVisibleItems) {
    return;
  }

  event.preventDefault();
  handleFitVisibleItems();
};
//------ 整屏显示全图 结束------

/**
 * 开关视图定向模式。
 */
const handleViewOrientationToggle = (): void => {
  const currentViewer = getViewer();
  const cameraManager = currentViewer?.cameraManager;
  if (!cameraManager) {
    return;
  }

  if (cameraManager.isViewOrientationFocusModeActive()) {
    cameraManager.exitViewOrientationFocusMode({ restoreCamera: true });
    isViewOrientationVisible.value = false;
    currentStandardViewType.value = null;
    return;
  }

  cameraManager.enterViewOrientationFocusMode();
  isViewOrientationVisible.value = cameraManager.isViewOrientationMeshVisible();
  currentStandardViewType.value = null;
};

/**
 * 切换到指定标准视图。
 *
 * @param viewType cad-core 标准视图类型。
 */
const handleQuickViewOrientationChange = (viewType: StandardViewType): void => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  clearQuickViewOrientationPreview();
  currentViewer.setStandardView?.(viewType);
  syncCurrentStandardViewType(currentViewer);
};

//------ 视图定向工具栏预览 开始------
/**
 * 请求显示工具栏下拉标准视图预览。
 *
 * @param viewType cad-core 标准视图类型。
 */
const handleQuickViewOrientationPreviewEnter = (viewType: StandardViewType): void => {
  /**
   * 右上角真实预览由 cad-core 统一渲染，工具栏只声明目标标准视图，
   * 避免 web-client 直接持有额外 canvas 或渲染器。
   */
  getViewer()?.setViewOrientationPreview?.(viewType);
};

/**
 * 关闭工具栏下拉标准视图预览。
 */
const clearQuickViewOrientationPreview = (): void => {
  getViewer()?.setViewOrientationPreview?.(null);
};
//------ 视图定向工具栏预览 结束------

onMounted(() => {
  disposePendingViewerCallback = setViewerCallback((viewer) => {
    registerViewportToolbarSubscriptions(viewer);
  });
  viewerCreatedSubscription = Viewer.viewerCreated$.subscribe((viewer) => {
    registerViewportToolbarSubscriptions(viewer);
  });
  //------ 整屏显示全图 开始------
  // F 键与工具栏按钮共享同一命令入口，避免快捷键链路出现单独行为。
  window.addEventListener('keydown', handleViewportToolbarKeydown);
  //------ 整屏显示全图 结束------
});

onUnmounted(() => {
  disposePendingViewerCallback?.();
  disposePendingViewerCallback = null;
  viewerCreatedSubscription?.unsubscribe();
  viewerCreatedSubscription = null;
  disposeRawPointerEventSubscriptions();
  unsubscribeSectionViewStateChanged?.();
  unsubscribeSectionViewStateChanged = null;
  //------ 整屏显示全图 开始------
  window.removeEventListener('keydown', handleViewportToolbarKeydown);
  //------ 整屏显示全图 结束------
});
</script>

<template>
  <div class="viewport-toolbar absolute top-6 z-100 flex w-full h-0 items-center justify-center gap-2">
    <div data-onboarding="view-tools" class="flex items-center justify-center gap-2">

    <div class="display-model-entry" data-onboarding="display-model">
      <n-popover placement="bottom-end" trigger="hover">
        <template #trigger>
          <n-button
              quaternary
              size="small"
              class="display-model-entry__main"
              :title="`显示模型：${currentDisplayModelModeItem.label}`"
          >
            <template #icon>
              <n-icon size="24">
                <CubeView />
              </n-icon>
            </template>
          </n-button>
        </template>

        <div class="display-model-menu" role="menu" aria-label="显示模型">
          <n-button
              v-for="displayModeItem in displayModelModeItems"
              :key="displayModeItem.mode"
              class="display-model-menu__item"
              :type="displayModelMode === displayModeItem.mode ? 'primary' : 'default'"
              :aria-label="displayModeItem.label"
              @click="handleDisplayModelModeChange(displayModeItem.mode)"
          >
            <span class="display-model-menu__label-cn">{{ displayModeItem.label }}</span>
          </n-button>
        </div>
      </n-popover>
    </div>

    <n-tooltip trigger="hover" placement="bottom">
      <template #trigger>
        <n-button
            quaternary
            size="small"
            title="整屏显示全图 (F)"
            data-onboarding="fit-view"
            @click="handleFitVisibleItems"
        >
          <template #icon>
            <n-icon size="24">
              <FitToScreen />
            </n-icon>
          </template>
        </n-button>
      </template>

      <div class="viewport-toolbar__tooltip">
        <div>整屏显示全图 (F)</div>
      </div>
    </n-tooltip>

    <n-tooltip trigger="hover" placement="bottom">
      <template #trigger>
        <n-button
            quaternary
            size="small"
            :type="isSectionViewEnabled || isSectionViewEditing ? 'primary' : 'default'"
            :disabled="readonlyPreview && !hasSectionViewDefinition"
            :aria-pressed="isSectionViewEnabled"
            aria-label="剖面视图"
            data-onboarding="section-view"
            title="剖面视图"
            @click="handleSectionViewToggle"
        >
          <template #icon>
            <n-icon size="24">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linejoin="round"
                    d="M4 5.5h16v13H4zM8 5.5v13m4-13v13m4-13v13"
                />
                <path fill="currentColor" opacity="0.28" d="M4 5.5h4v13H4zM12 5.5h4v13h-4z" />
              </svg>
            </n-icon>
          </template>
        </n-button>
      </template>

      <div class="viewport-toolbar__tooltip">
        <div>剖面视图</div>
        <div class="viewport-toolbar__tooltip-desc">
          {{ readonlyPreview ? '历史版本仅允许开关已保存剖面' : '使用基准面或平面实体面显示模型剖面' }}
        </div>
      </div>
    </n-tooltip>

    <div class="view-orientation-entry" data-onboarding="view-orientation">
      <n-popover placement="bottom-end" trigger="hover">
        <template #trigger>
          <n-button
              quaternary
              size="small"
              class="view-orientation-entry__main"
              :type="isViewOrientationVisible ? 'primary' : 'default'"
              title="视图定向"
              @click="handleViewOrientationToggle"
          >
            <template #icon>
              <n-icon size="24">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                      fill="currentColor"
                      d="M12 2 4 6v12l8 4 8-4V6zm0 2.18 5.82 2.91L12 10 6.18 7.09zM6 8.7l5 2.5v8.23l-5-2.5zm7 10.73V11.2l5-2.5v8.23z"
                  />
                </svg>
              </n-icon>
            </template>
          </n-button>
        </template>

        <div class="view-orientation-quick-switch">
          <n-button
              v-for="quickViewItem in quickViewOrientationItems"
              :key="quickViewItem.type"
              class="view-orientation-quick-switch__item max-h-100px h-5rem"
              :aria-label="quickViewItem.label"
              @mouseenter="handleQuickViewOrientationPreviewEnter(quickViewItem.type)"
              @mouseleave="clearQuickViewOrientationPreview"
              @focus="handleQuickViewOrientationPreviewEnter(quickViewItem.type)"
              @blur="clearQuickViewOrientationPreview"
              @click="handleQuickViewOrientationChange(quickViewItem.type)"
          >
            <div class="flex flex-col gap-1">
              <span
                  class="view-orientation-quick-switch__preview"
                  :class="`view-orientation-quick-switch__preview--${quickViewItem.previewKind}`"
                  aria-hidden="true"
              >
                  <svg viewBox="0 0 36 32" class="view-orientation-quick-switch__preview-svg">
                    <polygon class="view-orientation-quick-switch__face view-orientation-quick-switch__face--top" points="18,4 28,10 18,16 8,10" />
                    <polygon class="view-orientation-quick-switch__face view-orientation-quick-switch__face--main" points="8,10 18,16 18,28 8,22" />
                    <polygon class="view-orientation-quick-switch__face view-orientation-quick-switch__face--side" points="28,10 18,16 18,28 28,22" />
                    <polyline class="view-orientation-quick-switch__outline" points="18,4 28,10 18,16 8,10 18,4" />
                    <polyline class="view-orientation-quick-switch__outline" points="8,10 18,16 18,28 8,22 8,10" />
                    <polyline class="view-orientation-quick-switch__outline" points="28,10 18,16 18,28 28,22 28,10" />
                    <line class="view-orientation-quick-switch__outline" x1="18" y1="4" x2="18" y2="16" />
                    <line class="view-orientation-quick-switch__outline" x1="8" y1="10" x2="8" y2="22" />
                    <line class="view-orientation-quick-switch__outline" x1="28" y1="10" x2="28" y2="22" />
                  </svg>
              </span>
              <span class="view-orientation-quick-switch__label-cn">{{ quickViewItem.label }}</span>
              <span class="view-orientation-quick-switch__label-en">{{ quickViewItem.shortLabel }}</span>
            </div>
          </n-button>
        </div>
      </n-popover>
    </div>

      <div data-onboarding="projection-tools">
        <n-tooltip trigger="hover" placement="bottom">
          <template #trigger>
            <n-button
                quaternary
                size="small"
                :type="viewMode === CameraProjectionType.Orthographic ? 'primary' : undefined"
                @click="handleViewModeChange(CameraProjectionType.Orthographic)"
            >
              <template #icon>
                <n-icon size="24">
                  <DropPhoto />
                </n-icon>
              </template>
            </n-button>
          </template>

          正交视图
        </n-tooltip>

        <n-tooltip trigger="hover" placement="bottom">
          <template #trigger>
            <n-button
                quaternary
                size="small"
                class="ml-2"
                :type="viewMode === CameraProjectionType.Perspective ? 'primary' : undefined"
                @click="handleViewModeChange(CameraProjectionType.Perspective)"
            >
              <template #icon>
                <n-icon size="24">
                  <Camera />
                </n-icon>
              </template>
            </n-button>
          </template>

          透视视图
        </n-tooltip>
      </div>

    </div>
  </div>
</template>

<style scoped lang="less">
.viewport-toolbar__tooltip {
  font-size: 12px;
  line-height: 18px;
}

.viewport-toolbar__tooltip-desc {
  color: #8fa3c8;
}

/* ------ 显示模型下拉 开始------ */
.display-model-menu {
  width: 188px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.display-model-menu__item {
  width: 100%;
  min-height: 36px;
  border-radius: 8px;
  padding: 6px 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  text-align: left;
}

.display-model-menu__label-cn {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* ------ 显示模型下拉 结束------ */

.view-orientation-quick-switch {
  width: 192px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.view-orientation-quick-switch__item {
  border-radius: 8px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.view-orientation-quick-switch__preview {
  position: relative;
  width: 38px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-orientation-quick-switch__preview-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.view-orientation-quick-switch__face {
  fill: transparent;
  stroke: none;
}

.view-orientation-quick-switch__outline {
  fill: none;
  stroke: rgba(214, 224, 240, 0.92);
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.view-orientation-quick-switch__preview--front .view-orientation-quick-switch__face--main,
.view-orientation-quick-switch__preview--back .view-orientation-quick-switch__face--main,
.view-orientation-quick-switch__preview--left .view-orientation-quick-switch__face--main,
.view-orientation-quick-switch__preview--right .view-orientation-quick-switch__face--main {
  fill: var(--n-text-color-pressed);
}

.view-orientation-quick-switch__preview--top .view-orientation-quick-switch__face--top,
.view-orientation-quick-switch__preview--bottom .view-orientation-quick-switch__face--top {
  fill: var(--n-text-color-pressed);
}

.view-orientation-quick-switch__preview--front {
  transform: none;
}

.view-orientation-quick-switch__preview--back {
  transform: scaleX(-1);
  transform-origin: center;
}

.view-orientation-quick-switch__preview--left {
  transform: rotate(-90deg) scaleX(-1);
  transform-origin: center;
}

.view-orientation-quick-switch__preview--right {
  transform: rotate(90deg);
  transform-origin: center;
}

.view-orientation-quick-switch__preview--top {
  transform: none;
}

.view-orientation-quick-switch__preview--bottom {
  transform: rotate(180deg);
  transform-origin: center;
}

.view-orientation-quick-switch__label-cn {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
}

.view-orientation-quick-switch__label-en {
  font-size: 11px;
  line-height: 14px;
  color: #8fa3c8;
}
</style>
